import { useRouter } from 'hooks/useRouter'
import { useEffect, useMemo, useRef } from 'react'
import { ContentBlock } from './types'
import { Caption } from './types/heading'

export const CONTENT_BLOCK_PREFIX_ID = 'alchemy-content'

export const useGenerateCaptions = (
  editorContent: string,
): { captionsDisplay: Caption[]; contentBlocksDisplay: ContentBlock[] } => {
  const contentBlocks = useMemo(() => {
    try {
      return JSON.parse(editorContent)
    } catch (error) {
      console.error('Error:', error)
      return []
    }
  }, [editorContent])

  if (!contentBlocks.length) {
    return { captionsDisplay: [], contentBlocksDisplay: [] }
  }

  const checkDisplayCaption = (block) => block.data.caption?.isDisplayCaption && block.data.caption?.title

  const blocksWithId = useMemo(() => {
    return contentBlocks.map((block, index) => {
      const updatedBlock = { ...block }

      if (checkDisplayCaption(block)) {
        updatedBlock.data = {
          ...block.data,
          caption: {
            captionId: `${CONTENT_BLOCK_PREFIX_ID}-${index}`,
            title: block.data.caption.title,
          },
        }
      }

      return updatedBlock
    })
  }, [contentBlocks])

  const captionsDisplay = useMemo(
    () =>
      blocksWithId
        .filter((block) => block.data.caption?.captionId)
        .map((block) => ({
          title: block.data.caption.title,
          captionId: block.data.caption.captionId,
        })),
    [blocksWithId],
  )

  return {
    captionsDisplay,
    contentBlocksDisplay: blocksWithId,
  }
}

export const useHandleLinkEvent = () => {
  const contentRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  useEffect(() => {
    const container = contentRef.current
    if (!container) return

    const links = container.querySelectorAll('a')

    links.forEach((link) => {
      const href = link.getAttribute('href')
      if (!href) return

      const isSameOrigin = href.startsWith(window.location.origin)

      if (!isSameOrigin) return // external link, skip

      const handleClick = (e: MouseEvent) => {
        e.preventDefault()
        router.push(href)
      }

      link.addEventListener('click', handleClick)

      return () => {
        link.removeEventListener('click', handleClick)
      }
    })
  }, [router])

  return contentRef
}
